import { memo, useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useGlimpsedFiles } from "../../store/filteredFiles/hooks";
import { useWavesurfer } from "./useWavesurfer";

const SimpleAudioPlayer = (props) => {
  const { clip, height, id, onPause, onPlay } = props;
  const containerRef = useRef();
  const wavesurfer = useWavesurfer(containerRef, props);
  const [wavesurferReady, setWavesurferReady] = useState(false);

  const {
    activeFileId,
    clipTime,
    playState,
    volume,
    setActiveFilteredFiles,
    setClipTimeFilteredFiles,
    setPlayStateFilteredFiles,
  } = useGlimpsedFiles();

  useEffect(() => {
    if (!wavesurfer) return;
    const wsPlay = () => {
      if (onPlay) onPlay();
    };
    const wsPause = () => {
      if (onPause) onPause();
    };
    wavesurfer.on("play", wsPlay);
    wavesurfer.on("pause", wsPause);
    return () => {
      wavesurfer.un("play", wsPlay);
      wavesurfer.un("pause", wsPause);
    };
  }, [wavesurfer, onPlay, onPause]);

  //Ready/Load
  useEffect(() => {
    if (!wavesurfer) return;
    const wsReady = () => {
      setWavesurferReady(true);
    };
    const wsLoading = () => {
      setWavesurferReady(false);
    };
    wavesurfer.on("ready", wsReady);
    wavesurfer.on("loading", wsLoading);
    return () => {
      wavesurfer.un("ready", wsReady);
      wavesurfer.un("loading", wsLoading);
    };
  }, [wavesurfer]);

  useEffect(() => {
    //Do this for all players
    if (wavesurfer) {
      wavesurfer.setVolume(volume / 100);
    }
  }, [wavesurfer, volume]);

  useEffect(() => {
    if (wavesurfer) {
      const isActive = activeFileId === id;
      //Force all to pause because of the playstate change
      wavesurfer.pause();

      if (isActive) {
        switch (playState) {
          case "PLAY": {
            wavesurfer.play();
            break;
          }
          case "STOP": {
            wavesurfer.stop();
            break;
          }
          default: {
            wavesurfer.stop();
            break;
          }
        }
      }
    }
  }, [wavesurfer, playState, activeFileId, id]);

  useEffect(() => {
    if (wavesurfer) {
      const isActive = activeFileId === id;
      const color = isActive ? "rgb(31, 78, 179)" : "rgb(200, 200, 200)";

      wavesurfer.setOptions({
        ...wavesurfer.options,
        waveColor: color,
        progressColor: color,
      });
    }
  }, [wavesurfer, activeFileId, id]);

  //Time update effect
  useEffect(() => {
    if (!wavesurfer) return;

    const wsPlay = () => setActiveFilteredFiles(id);

    const timeUpdate = () => {
      if (wavesurfer.isPlaying) {
        const currentTime = wavesurfer.getCurrentTime();
        if (Math.abs(currentTime - clipTime) > 0.15) {
          setClipTimeFilteredFiles(currentTime, clip);
        }
      }
    };

    const wsFinish = () => setPlayStateFilteredFiles("PAUSE");

    wavesurfer.on("timeupdate", timeUpdate);
    wavesurfer.on("play", wsPlay);
    wavesurfer.on("finish", wsFinish);
    return () => {
      wavesurfer.un("timeupdate", timeUpdate);
      wavesurfer.un("play", wsPlay);
      wavesurfer.un("finish", wsFinish);
    };
  }, [
    wavesurfer,
    activeFileId,
    clipTime,
    id,
    clip,
    setActiveFilteredFiles,
    setClipTimeFilteredFiles,
    setPlayStateFilteredFiles,
  ]);

  //Time update effect
  useEffect(() => {
    if (wavesurfer) {
      if (!wavesurfer.isPlaying()) {
        wavesurfer.setTime(clipTime);
      } else if (Math.abs(wavesurfer.getCurrentTime() - clipTime) > 0.5) {
        wavesurfer.setTime(clipTime);
      }
    }
  }, [wavesurfer, clipTime]);

  return (
    <Box
      sx={{
        height: height,
        width: 1,
      }}
    >
      {!wavesurferReady && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-around",
            width: 1,
            height: height,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Box
        ref={containerRef}
        sx={{
          height: height,
          width: 1,
        }}
      />
    </Box>
  );
};

export default memo(SimpleAudioPlayer);
